<script setup lang="ts">
import type { Company } from "@asap/shared/schemas/company";
import { useCompanyApi } from "~/components/company/composables/useCompanyApi";
import ConfirmModal from "~/components/shared/modal/ConfirmModal.vue";
import { useToast } from "~/composables/useToast";

const props = defineProps<{
  company: Company;
}>();

const emit = defineEmits<{
  onUpdate: [company: Company];
}>();

const { t } = useI18n();
const { updateCompanyRisk } = useCompanyApi();
const { addToastError, addToastSuccess } = useToast();

const isUpdating = ref(false);
const isOpen = ref(false);

const updateRisk = async () => {
  try {
    isUpdating.value = true;
    const updatedCompany = await updateCompanyRisk(props.company.id, { risk_level: "none" });
    isOpen.value = false;
    addToastSuccess({ description: t("companies.removeRiskModal.toast.removed", { name: props.company.name }) });
    emit("onUpdate", updatedCompany);
  } catch (error) {
    addToastError(
      { title: t("companies.removeRiskModal.toast.removeError"), description: t("companies.toast.contactSupport") },
      error
    );
  } finally {
    isUpdating.value = false;
  }
};
</script>

<template>
  <AwButton size="sm" color="secondary" class="h-6" :is-loading="isUpdating" @click="isOpen = true">
    {{ $t("companies.removeRiskModal.removeAlert") }}
  </AwButton>

  <ConfirmModal
    :is-open="isOpen"
    :title="$t('companies.removeRiskModal.title', { name: company.name })"
    :description="$t('companies.removeRiskModal.description')"
    :primary-label="$t('companies.removeRiskModal.removeAlert')"
    :is-confirming="isUpdating"
    @update:open="isOpen = $event"
    @update:on-confirm="updateRisk"
  />
</template>
